import {
  Section,
  Cell,
  Avatar,
  Caption,
  Subheadline,
  Headline,
  Button,
} from '@telegram-apps/telegram-ui';
import {FC, useEffect, useState} from 'react';

import { Page } from '@/components/Page.tsx';
import {closeMiniApp, openTelegramLink, setMiniAppHeaderColor} from "@telegram-apps/sdk-react";
import {useAuthProfile} from "@/providers/AuthProfileProvider.tsx";
import {
  TbChevronRight,
  TbGift,
  TbPlus, TbSearch, TbSettings, TbShare3, TbSparkles,
} from "react-icons/tb";
import {useNavigation} from "@/providers/NavigationProvider.tsx";
import {ModalDatePicker} from "@/components/ModalDatePicker/ModalDatePicker.tsx";
import {startSearchProfiles, updateUserSettings} from "@/api/apiMethods.ts";
import {useTranslation} from "react-i18next";
import {WishlistCell} from "@/components/Wishlist/WIshlistCell.tsx";
import {renderWishesCount} from "@/helpers/translate/i18n.ts";
import {requestWriteAccess} from "@telegram-apps/sdk-react";
import {Recos} from "@/components/Idea/Recos.tsx";
import {haptic} from "@/helpers/haptic.ts";
import {getUserAcronym} from "@/helpers/getUserAcronym.ts";

export const MainPage: FC = () => {
  const { authProfile, setAuthProfile } = useAuthProfile();
  const { navigateToRoute, showSnackbar } = useNavigation();
  const [ isDatePickerOpen, setIsDatePickerOpen ] = useState<boolean>(false);
  const { t, i18n: {language} } = useTranslation();

  if (!authProfile) {
    throw new Error("MainPage must be inside the AuthProfileProvider");
  }

  useEffect(()=>{
    if (setMiniAppHeaderColor.supports.rgb()) {
      const color = getComputedStyle(document.documentElement)
        .getPropertyValue('--tg-theme-section-bg-color');
      // @ts-expect-error it's ok
      setMiniAppHeaderColor(color);
    }
    return () => {
      if (setMiniAppHeaderColor.supports.rgb()) {
        const color = getComputedStyle(document.documentElement)
          .getPropertyValue('--tg-theme-secondary-bg-color');
        // @ts-expect-error it's ok
        setMiniAppHeaderColor(color);
      }
    };
  })

  const formatDateString = (date: Date | null) => {
    if (!date) {
      return null;
    }
    return date.toLocaleDateString(language, {year: "numeric", month: "long", day: "numeric"});
  }

  const openDatePicker = () => {
    haptic.impact('light');
    setIsDatePickerOpen(true);
  }

  const onBirthdaySaved = async (date: Date) => {
    try {
      const user = authProfile.user;
      const newUserSettings = {
        language: user.language,
        showBookings: user.showBookings,
        showTgUsername: user.showTgUsername,
        countryCode: user.countryCode,
        city: user.city,
        birthday: date,
      }
      const updatedSettings = await updateUserSettings(newUserSettings);
      const newUser = {...user, ...updatedSettings};
      setAuthProfile({...authProfile, user: newUser});
      setIsDatePickerOpen(false);
      haptic.notification('success');
      showSnackbar("✅", "system.birthdaySaved");
    } catch (e) {
      haptic.notification('error');
      showSnackbar("❌", "system.unknowError");
    }
  }

  const onBirthdayCancel = () => {
    setIsDatePickerOpen(false);
  }

  const startFindFriends = () => {
    haptic.impact('light');
    requestWriteAccess().then((status)=>{
      if (status === 'allowed') {
        startSearchProfiles().then(()=>{
          openTelegramLink(import.meta.env.VITE_BOT_LINK);
          closeMiniApp();
        })
      }
    });

  }

  return (
    <Page>
      <div className={"flex flex-col gap-3 pb-10"}>
        <div className={"p-3 pb-3 rounded-b-xl bg-section flex flex-col gap-3"}>
          <div className={"flex justify-center pb-3"}>
            <div className={"flex gap-3 items-center"}>
              <Avatar size={96}
                      acronym={getUserAcronym(authProfile.user.fullname)}
                      src={authProfile.user.photo?.sizes[0]?.link}></Avatar>
              <div className={"w-full"}>
                <Subheadline level={"2"} className={"text-color-hint"}>{t('profile.titleYourProfile')}</Subheadline>
                <Headline className={"tg-link-color"}>
                  {authProfile.user.fullname}
                </Headline>
                <Caption onClick={openDatePicker}>
                  {t('profile.birthday')}: {formatDateString(authProfile.user.birthday) ??
                  <Button mode={"plain"} size={"s"}>
                    {t('profile.actionFillBirthday')}
                  </Button>}
                </Caption>
              </div>
            </div>
          </div>
          <Button stretched={true} mode={"gray"}
                  onClick={() => {
                    navigateToRoute('/main/settings');
                  }}
                  before={<TbSettings size={20}/>}>
            {t('profile.settings')}
          </Button>
          <Button
            after={<TbShare3 size={22} />}
            mode={"filled"}
            className={"shiny-btn-primary"}
            onClick={async () => {
              haptic.impact('soft');
              const profileLink = import.meta.env.VITE_MINIAPP_URL + `?startapp=-u${authProfile.user.id}`;
              const { shareLink } = await import('@/helpers/shareLink');
              await shareLink(profileLink);
            }}
          >
            {t('action.shareProfile')}
          </Button>
          <Cell after={<TbChevronRight size={20}/>}
                before={<TbGift size={20}/>}
                className={"tg-link-color cell-compact-padding"}
                onClick={()=>{
                  navigateToRoute('/main/wishes/all');
                }}
          >
            {t('profile.allYourWishes')} ({authProfile.allWishes.length})
          </Cell>
          <Cell after={<TbChevronRight size={20}/>}
                before={<TbSparkles size={20}/>}
                className={"tg-link-color cell-compact-padding"}
                onClick={()=>{
                  navigateToRoute('/main/wishes/gifted');
                }}
          >
            {t('profile.giftedWishes')}
          </Cell>
          {/*<Cell after={<TbChevronRight size={20}/>}*/}
          {/*      before={<TbSnowflake size={20}/>}*/}
          {/*      className={"tg-link-color cell-compact-padding"}*/}
          {/*      onClick={()=>navigateToRoute('/main/secret-santa')}*/}
          {/*>*/}
          {/*  {t('secretSanta.title')}*/}
          {/*</Cell>*/}
          {/*<Cell after={<TbChevronRight size={20}/>}*/}
          {/*      before={<TbChecklist size={20}/>}*/}
          {/*      className={"tg-link-color cell-compact-padding"}*/}
          {/*>*/}
          {/*  {t('profile.giftedWishes')}*/}
          {/*</Cell>*/}
        </div>

        <Section header={t('profile.titleYourWishlists')}>
          {authProfile.wishlistPreviews.map((wishlist) => (
            <WishlistCell key={"myWishlistPreview_"+wishlist.id} wishlist={wishlist}/>
          ))}

          <Button stretched={true} className={"mb-1"}
                  onClick={() => {
                    navigateToRoute(`/main/wishlists/new`);
                  }}
                  before={<TbPlus size={22} />}
                  mode={"plain"}>
            {t('wishlist.action.create')}
          </Button>
        </Section>

        <Section header={t('profile.subscribes')}>
          {authProfile.subscribedWishlistPreviews.map((wishlist) => (
            <Cell key={"subscribed_wishlist_"+wishlist.id}
                  description={<div className={"flex items-center gap-1 p-1 pl-0.5"}>
                    <Avatar size={20} src={wishlist.owner.photo?.sizes[0]?.link} acronym={getUserAcronym(wishlist.owner.fullname)}/>
                    {wishlist.owner.fullname}
                  </div>}
                  after={<Caption weight={"2"} className={"flex gap-1 items-center tg-link-color text-center"}>
                    {renderWishesCount(wishlist.wishesCount)}
                    <TbChevronRight size={20}/>
                  </Caption>}
                  onClick={() => {
                    navigateToRoute(`/wishlists/${wishlist.id}`);
                  }}>
              {wishlist.title}
            </Cell>
          ))}

          {authProfile.subscribedWishlistPreviews.length === 0 &&
            <div className={"p-5 text-center"}>
              <Caption className={"text-color-hint"}>
                {t('state.noSubscribes')}
              </Caption>
            </div>
          }

          <Button stretched={true} className={"mb-1"}
                  onClick={startFindFriends}
                  before={<TbSearch size={22} />}
                  mode={"plain"}>
            {t('profile.findFriends')}
          </Button>

        </Section>

        {<Recos ideas={authProfile.ideas}/>}
      </div>
      <ModalDatePicker
        initialDate={authProfile.user.birthday}
        opened={isDatePickerOpen}
        onApply={onBirthdaySaved}
        onCancel={onBirthdayCancel}/>
    </Page>
  );
};
